/**
 * FlightProfile — the scrub control.
 *
 * This is deliberately not a progress bar with a chart above it. The altitude
 * trace IS the track: the vertical axis is real height, event pips sit at
 * their true time, and dragging anywhere in the trace moves through the
 * flight. One artifact doing the job of chart, timeline and transport.
 *
 * The point is that the shape of the flight is legible before you touch it.
 * You can see the climb, the loiter, where the obstacle warning fired, and
 * where the battery got low, all without pressing play.
 */

import { fmt, units } from './contract.js';

const SERIES_KEY = 'ddlv-profile-series';

/**
 * What the strip can draw.
 *
 * Height is the filled one because it is the thing people look for first.
 * Battery is dashed because it only ever falls and the shape carries no
 * surprises. Distance from home is a plain line: read against height it
 * separates an orbit from an out-and-back without reading a single figure.
 */
const SERIES = [
  {
    id: 'height',
    key: 'heightM',
    label: 'height',
    colour: 'var(--trace)',
    fill: 'rgba(87,199,232,0.16)',
    unit: () => units.label('altitude'),
    fmt: (v) => (units.system === 'imperial' ? v * 3.28084 : v).toFixed(1),
  },
  {
    id: 'distance',
    key: 'homeDistanceM',
    label: 'distance',
    colour: 'var(--ok)',
    min: 0,
    opacity: 0.85,
    unit: () => units.label('distance'),
    fmt: (v) => `${Math.round(units.system === 'imperial' ? v * 3.28084 : v)}`,
  },
  {
    id: 'speed',
    key: 'speedHorizontalMs',
    label: 'speed',
    colour: 'var(--amber)',
    min: 0,
    opacity: 0.85,
    unit: () => units.label('speed'),
    fmt: (v) => (units.system === 'imperial' ? v * 2.23694 : v).toFixed(1),
  },
];

const SEVERITY_COLOUR = {
  critical: 'var(--warn)',
  warning: 'var(--caution)',
  info: 'var(--trace-deep)',
};

export class FlightProfile {
  constructor(container, flight, onSeek) {
    this.container = container;
    this.flight = flight;
    this.onSeek = onSeek;
    this.index = 0;
    this.playing = false;
    this.speed = 1;
    this._raf = null;
    this._lastFrame = 0;
    this._playTime = 0;

    this.render();
    this.bind();
  }

  render() {
    const width = 1000;
    const height = 68;
    const padTop = 8;
    const padBottom = 14;
    const plotHeight = height - padTop - padBottom;

    const { timeS } = this.flight.cols;
    const count = this.flight.count;
    const duration = timeS[count - 1] || 1;

    const x = (i) => (timeS[i] / duration) * width;

    // Draw at a sane density rather than one point per sample. At ~10 Hz
    // decimated to 1 Hz a long flight is still ~1500+ points, and packing them
    // into an ~850px strip turns per-sample GPS/height jitter into visual noise
    // (the report avoids this by sampling ~120 points). Target ~250 points; the
    // shape is preserved, the noise is not.
    const step = Math.max(1, Math.ceil(count / 250));

    // Each series is scaled to its own range rather than a shared axis. They
    // are in different units and there is no meaningful common scale between
    // metres, percent and metres-from-home. What the strip conveys is shape:
    // where the climb was, where the battery fell away, how far out it went.
    const series = SERIES.filter((s) => this.flight.has(s.key));

    const paths = series
      .map((spec) => {
        const values = this.flight.cols[spec.key];
        const low = spec.min !== undefined ? spec.min : this.lowest(values);
        const high = spec.max !== undefined ? spec.max : this.highest(values);
        const span = high - low || 1;

        const y = (v) => padTop + plotHeight - ((v - low) / span) * plotHeight;

        let d = '';

        for (let i = 0; i < count; i += step) {
          const value = values[i];
          if (value === null) continue;

          d += `${d === '' ? 'M' : ' L'} ${x(i).toFixed(1)} ${y(value).toFixed(1)}`;
        }

        if (d === '') return '';

        const area = spec.fill
          ? `<path class="series series-${spec.id}" data-series="${spec.id}"
               d="${d} L ${width} ${padTop + plotHeight} L 0 ${padTop + plotHeight} Z"
               fill="${spec.fill}" stroke="none" />`
          : '';

        return area + `<path class="series series-${spec.id}" data-series="${spec.id}"
            d="${d}" fill="none" stroke="${spec.colour}" stroke-width="1"
            ${spec.dash ? `stroke-dasharray="${spec.dash}"` : ''}
            vector-effect="non-scaling-stroke" opacity="${spec.opacity || 1}" />`;
      })
      .join('');

    const pips = this.flight.events
      .filter((e) => e.severity !== 'info' || e.source === 'app')
      .map((e) => {
        const px = x(e.index).toFixed(1);
        const colour = SEVERITY_COLOUR[e.severity] || SEVERITY_COLOUR.info;
        return `<line x1="${px}" y1="${padTop}" x2="${px}" y2="${padTop + plotHeight}"
                  stroke="${colour}" stroke-width="1" opacity="0.45" />
                <circle cx="${px}" cy="${padTop + plotHeight + 5}" r="2.5" fill="${colour}" />`;
      })
      .join('');

    const legend = series
      .map(
        (spec) => `<button type="button" class="strip-key" data-toggle="${spec.id}"
            aria-pressed="true" title="Show or hide ${spec.label}">
            <span class="swatch" style="background:${spec.colour}"></span>
            <span class="k-label">${spec.label} <span class="k-unit" data-unit="${spec.id}"></span></span>
            <span class="k-value num" data-value="${spec.id}">—</span>
          </button>`
      )
      .join('');

    this.container.innerHTML = `
	  <div class="transport">
        <div class="time-display">
          <span class="elapsed num">0:00</span>
          <span class="total num">/ ${fmt.elapsed(duration)}</span>
        </div>
        <div class="transport-buttons">
          <button type="button" data-act="play" aria-label="Play flight">${ICON_PLAY}</button>
          <button type="button" class="speed" data-act="speed">1x</button>
        </div>
      </div>
      <div class="profile-track" tabindex="0" role="slider"
           aria-label="Flight timeline"
           aria-valuemin="0" aria-valuemax="${count - 1}" aria-valuenow="0">
        <svg viewBox="0 0 ${width} ${height}" preserveAspectRatio="none" aria-hidden="true">
          ${paths}
          ${pips}
          <line class="playhead" x1="0" y1="0" x2="0" y2="${height}"
                stroke="var(--text)" stroke-width="1" vector-effect="non-scaling-stroke" />
        </svg>
      </div>

      <div class="profile-meta">
        <div class="strip-keys">${legend}</div>
      </div>
    `;

    this.track = this.container.querySelector('.profile-track');
    this.playhead = this.container.querySelector('.playhead');
    this.elapsed = this.container.querySelector('.elapsed');
    this.playButton = this.container.querySelector('[data-act="play"]');
    this.speedButton = this.container.querySelector('[data-act="speed"]');
    this._width = width;
    this._duration = duration;
    this._series = series;

    this.applyVisibility();
    this.updateValues();
  }

  /** Repaint each key's value for the current row, in the active unit system. */
  updateValues() {
    if (!this._series) return;
    for (const spec of this._series) {
      const v = this.flight.cols[spec.key][this.index];
      const valNode = this.container.querySelector(`[data-value="${spec.id}"]`);
      if (valNode) valNode.textContent = v === null || v === undefined ? '—' : spec.fmt(v);
      if (typeof spec.unit === 'function') {
        const unitNode = this.container.querySelector(`[data-unit="${spec.id}"]`);
        if (unitNode) unitNode.textContent = `(${spec.unit()})`;
      }
    }
  }

  lowest(values) {
    let low = null;
    for (const v of values) if (v !== null && (low === null || v < low)) low = v;
    return low === null ? 0 : Math.min(0, low);
  }

  highest(values) {
    let high = null;
    for (const v of values) if (v !== null && (high === null || v > high)) high = v;
    return high === null ? 1 : high;
  }

  /**
   * Which series are drawn.
   *
   * Three traces in a strip this short can turn to mush, so each one can be
   * switched off. The choice persists: someone who does not care about
   * battery should not have to say so on every flight.
   */
  visible() {
    try {
      const stored = localStorage.getItem(SERIES_KEY);
      if (stored) return new Set(JSON.parse(stored));
    } catch {
      /* storage may be unavailable or hold something stale */
    }

    return new Set(SERIES.map((s) => s.id));
  }

  applyVisibility() {
    const shown = this.visible();

    for (const node of this.container.querySelectorAll('[data-series]')) {
      node.classList.toggle('series-off', !shown.has(node.dataset.series));
    }

    for (const button of this.container.querySelectorAll('[data-toggle]')) {
      const on = shown.has(button.dataset.toggle);
      button.setAttribute('aria-pressed', String(on));
      button.classList.toggle('off', !on);
    }
  }

  toggleSeries(id) {
    const shown = this.visible();

    if (shown.has(id)) {
      shown.delete(id);
    } else {
      shown.add(id);
    }

    try {
      localStorage.setItem(SERIES_KEY, JSON.stringify([...shown]));
    } catch {
      /* a preference that cannot be saved is still worth applying */
    }

    this.applyVisibility();
  }

  bind() {
    let dragging = false;

    const seekFromPointer = (event) => {
      const rect = this.track.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
      this.seek(this.flight.indexAtTime(ratio * this._duration));
    };

    this.track.addEventListener('pointerdown', (event) => {
      dragging = true;
      this.track.setPointerCapture(event.pointerId);
      this.pause();
      seekFromPointer(event);
    });

    this.track.addEventListener('pointermove', (event) => {
      if (dragging) seekFromPointer(event);
    });

    this.track.addEventListener('pointerup', (event) => {
      dragging = false;
      this.track.releasePointerCapture(event.pointerId);
    });

    // Keyboard parity with the pointer. A slider that cannot be driven from
    // the keyboard is not a slider.
    this.track.addEventListener('keydown', (event) => {
      const big = Math.max(1, Math.round(this.flight.count / 20));
      const moves = {
        ArrowLeft: -1, ArrowRight: 1,
        ArrowDown: -1, ArrowUp: 1,
        PageDown: -big, PageUp: big,
        Home: -this.flight.count, End: this.flight.count,
      };

      if (event.key === ' ') {
        event.preventDefault();
        this.toggle();
        return;
      }

      const delta = moves[event.key];
      if (delta === undefined) return;

      event.preventDefault();
      this.pause();
      this.seek(this.index + delta);
    });

    this.playButton.addEventListener('click', () => this.toggle());

    for (const button of this.container.querySelectorAll('[data-toggle]')) {
      button.addEventListener('click', () => this.toggleSeries(button.dataset.toggle));
    }

    this.speedButton.addEventListener('click', () => {
      const speeds = [1, 2, 4, 8];
      this.speed = speeds[(speeds.indexOf(this.speed) + 1) % speeds.length];
      this.speedButton.textContent = `${this.speed}x`;
    });
  }

  /**
   * @param {number} index
   * @param {boolean} fromPlayback true when the clock drove this, not the user
   */
  /**
   * Where the playhead is, in rows, including the part between two rows.
   */
  fractionAt(timeSeconds) {
    const { timeS } = this.flight.cols;
    const index = this.flight.indexAtTime(timeSeconds);
    const here = timeS[index];
    const next = timeS[index + 1];

    if (here === null || next === null || next === undefined || next <= here) {
      return index;
    }

    return index + Math.max(0, Math.min(1, (timeSeconds - here) / (next - here)));
  }

  /**
   * @param {number} index
   * @param {boolean} fromPlayback true when the clock drove this, not the user
   * @param {number|undefined} fraction position including the part between
   *   rows, supplied only during playback
   */
  seek(index, fromPlayback = false, fraction = undefined) {
    const clamped = Math.max(0, Math.min(this.flight.count - 1, Math.round(index)));

    // A manual seek re-anchors the playback clock, otherwise pressing play
    // after dragging would jump back to wherever the clock had got to.
    if (!fromPlayback) {
      this._playTime = this.flight.cols.timeS[clamped] || 0;
    }

    // The fraction still has to reach the follower even when the row has not
    // changed, or a smooth follow stalls between rows and stutters.
    if (clamped === this.index) {
      if (this.onSeek && fraction !== undefined) this.onSeek(clamped, fraction);

      return;
    }

    this.index = clamped;
    this.update();
    if (this.onSeek) this.onSeek(clamped, fraction);
  }

  update() {
    const t = this.flight.cols.timeS[this.index] || 0;
    const x = (t / this._duration) * this._width;

    this.playhead.setAttribute('x1', x);
    this.playhead.setAttribute('x2', x);
    this.elapsed.textContent = fmt.elapsed(t);
    this.track.setAttribute('aria-valuenow', String(this.index));
    this.track.setAttribute('aria-valuetext', `${fmt.elapsed(t)} of ${fmt.elapsed(this._duration)}`);
    this.updateValues();
  }

  toggle() {
    if (this.playing) this.pause();
    else this.play();
  }

  play() {
    if (this.playing) return;

    // Restarting from the end is what the user means by pressing play there.
    if (this.index >= this.flight.count - 1) this.seek(0);

    this.playing = true;
    if (this.onPlayStateChange) this.onPlayStateChange(true);
    this.playButton.innerHTML = ICON_PAUSE;
    this.playButton.setAttribute('aria-label', 'Pause flight');
    this._lastFrame = performance.now();
    this._playTime = this.flight.cols.timeS[this.index] || 0;

    const frame = (now) => {
      if (!this.playing) return;

      // The playback clock is accumulated separately from the row index, and
      // that separation is the whole trick.
      //
      // Deriving the next target from the CURRENT ROW's timestamp deadlocks:
      // one frame is ~16ms, log rows are 100ms apart, so the nearest row to
      // "now + 16ms" is the row we are already on. seek() no-ops, the index
      // never moves, and the next frame computes the same 16ms step from the
      // same unchanged row. Playback sits still forever.
      this._playTime += ((now - this._lastFrame) / 1000) * this.speed;
      this._lastFrame = now;

      if (this._playTime >= this._duration) {
        this.seek(this.flight.count - 1, true);
        this.pause();
        return;
      }

      // The fractional position, not just the row.
      //
      // Log rows are 100 ms apart, so the row index changes ten times a
      // second while the display refreshes sixty. Anything that follows the
      // integer index therefore moves in ten visible steps a second. Passing
      // the position between rows lets a follower run at the refresh rate.
      this.seek(this.flight.indexAtTime(this._playTime), true, this.fractionAt(this._playTime));
      this._raf = requestAnimationFrame(frame);
    };

    this._raf = requestAnimationFrame(frame);
  }

  pause() {
    if (!this.playing) return;
    this.playing = false;
    if (this.onPlayStateChange) this.onPlayStateChange(false);
    if (this._raf) cancelAnimationFrame(this._raf);
    this.playButton.innerHTML = ICON_PLAY;
    this.playButton.setAttribute('aria-label', 'Play flight');
  }

  destroy() {
    this.pause();
    this.container.innerHTML = '';
  }
}

const ICON_PLAY =
  '<svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true"><path d="M2.5 1.5v9l8-4.5z"/></svg>';
const ICON_PAUSE =
  '<svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true"><rect x="2.5" y="1.5" width="2.6" height="9"/><rect x="6.9" y="1.5" width="2.6" height="9"/></svg>';
